import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Moment from 'moment';
import APIs from '../../Constants/apis';
import axiosInstance from '../../config/axiosApi/axiosApi';
import useIsChildBranch from '../../hooks/useIsChildBranch';

const GlobalSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const searchRef = useRef(null);
  const resultsRef = useRef(null);
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const isBranchMode = useIsChildBranch();
  const [compCurrency, setCompCurrency] = useState("GBP");

  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return Moment(dateString).format('MMM DD, YYYY HH:mm');
    } catch (error) {
      return dateString;
    }
  };

  const fetchCompCurrency = useCallback(async () => {
    try {
      if (!profileData) {
        setCompCurrency("GBP");
        return;
      }
      setCompCurrency(profileData?.compCurrency || "GBP");
    } catch (err) {
      setCompCurrency("GBP");
    }
  }, [profileData]);

  const performSearch = useCallback(async (query) => {
    if (!CompID) return;

    setIsSearching(true);
    try {
      const response = await axiosInstance.get(APIs.GLOBAL_SEARCH, {
        params: { CompID, searchTerm: query }
      });

      if (response.data && response.data.status && response.data.data) {
        const { customers, orders, products, categories, discounts, modifierGroups, staff } = response.data.data;

        const results = [];

        if (customers && customers.length > 0) {
          results.push({
            type: 'customers',
            title: 'Customers',
            icon: 'fa fa-users',
            items: customers.map(customer => ({
              id: customer.cust_Guid || '',
              title: `${customer.cust_Fname || ''} ${customer.cust_Lname || ''}`.trim(),
              subtitle: customer.cust_Email || customer.cust_Phone || 'Customer',
              link: `/customer/updateCustomer/${customer.cust_Guid || ''}`,
              type: 'customer'
            }))
          });
        }

        if (orders && orders.length > 0) {
          results.push({
            type: 'orders',
            title: 'Orders',
            icon: 'fa fa-shopping-cart',
            items: orders.map(order => ({
              id: order.recvorder_ID || order.recvOrder_ID,
              title: `Order #${order.recvorder_InvoiceNo || order.recvorder_InvoiceNo} - ${order.recvorder_FrontOrderID || order.recvorder_FrontOrderID}`,
              subtitle: `${getCurrencySymbol(compCurrency)}${order.recvorder_Totalamount || order.recvorder_Totalamount || '0.00'} - ${formatDate(order.recvorder_datetime || order.recvorder_datetime)}`,
              link: `/order/orderDetails/${order.recvorder_InvoiceNo ||order.recvorder_InvoiceNo || 0}`,
              type: 'order'
            }))
          });
        }

        if (products && products.length > 0) {
          results.push({
            type: 'products',
            title: 'Products',
            icon: 'fa fa-box',
            items: products.map(product => ({
              id: product.menuItem_ID || product.menuItem_ID,
              title: product.menuItem_Name || product.menuItem_Name,
              subtitle: `${getCurrencySymbol(compCurrency)} ${product.menuItem_SalePrice || product.menuItem_SalePrice || '0.00'}`,
              // For branches, redirect to branchEditItem instead of updateItem
              link: isBranchMode 
                ? `/items/branchEditItem/${product.menuItem_ID || product.menuItem_ID}`
                : `/items/updateItem/${product.menuItem_ID || product.menuItem_ID}`,
              type: 'product'
            }))
          });
        }

        if (categories && categories.length > 0) {
          results.push({
            type: 'categories',
            title: 'Categories',
            icon: 'fa fa-tags',
            items: categories.map(category => ({
              id: category.rootCat_ID || category.rootCat_ID,
              title: category.rootCat_Name || category.rootCat_Name,
              subtitle: category.rootCat_Detail || category.rootCat_Detail,
              // Allow navigation for branches, but will be view-only on detail page
              link: `/categories/updateCategory/${category.rootCat_ID || category.rootCat_ID}`,
              type: 'category',
              isViewOnly: isBranchMode // Mark as view-only for branches
            }))
          });
        }

        if (discounts && discounts.length > 0) {
          results.push({
            type: 'discounts',
            title: 'Discounts',
            icon: 'fa fa-percent',
            items: discounts.map(discount => ({
              id: discount.id || discount.id,
              title: discount.discountName || discount.discountName,
              subtitle: `${discount.discountPercentage || discount.discountPercentage || '0'}% - ${discount.minimumSpendAmount || discount.minimumSpendAmount || ''}`,
              // Allow navigation for branches, but will be view-only on detail page
              link: `/discount/updateDiscount/${discount.id || discount.id}`,
              type: 'discount',
              isViewOnly: isBranchMode // Mark as view-only for branches
            }))
          });
        }

        if (modifierGroups && modifierGroups.length > 0) {
          results.push({
            type: 'modifierGroups',
            title: 'Modifier Groups',
            icon: 'fa fa-list',
            items: modifierGroups.map(group => ({
              id: group.modifierGroupID || group.modifierGroupID,
              title: group.name || group.name,
              subtitle: group.subTitle || group.subTitle || 'Modifier Group',
              // Allow navigation for branches, but will be view-only on detail page
              link: `/Add-ons/updateModifierGroup/${group.modifierGroupID || group.modifierGroupID}`,
              type: 'modifierGroup',
              isViewOnly: isBranchMode // Mark as view-only for branches
            }))
          });
        }

        if (staff && staff.length > 0) {
          results.push({
            type: 'staff',
            title: 'Staff',
            icon: 'fa fa-user-tie',
            items: staff.map(staff => ({
              id: staff.userName || staff.userName,
              title: `${staff.firstName || staff.firstName || ''} ${staff.lastName || staff.lastName || ''}`.trim(),
              subtitle: staff.email || staff.email || staff.position || staff.position || 'Staff',
              link: `/team/updateTeamMember/${staff.userName || staff.userName}`,
              type: 'staff'
            }))
          });
        }

        setSearchResults(results);
        setShowResults(results.length > 0);
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    } catch (error) {
      setSearchResults([]);
      setShowResults(false);
    } finally {
      setIsSearching(false);
    }
  }, [CompID, isBranchMode, compCurrency]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        performSearch(searchQuery.trim());
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, performSearch]);

  useEffect(() => {
    fetchCompCurrency();
  }, [fetchCompCurrency]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e) => {
    if (!showResults || searchResults.length === 0) return;

    const totalItems = searchResults.reduce((sum, section) => sum + section.items.length, 0);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % totalItems);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev <= 0 ? totalItems - 1 : prev - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          const selectedItem = getSelectedItem();
          if (selectedItem && selectedItem.link && selectedItem.link !== '#') {
            setShowResults(false);
            setSearchQuery('');
            window.location.href = selectedItem.link;
          }
        }
        break;
      case 'Escape':
        setShowResults(false);
        setSelectedIndex(-1);
        setSearchQuery('');
        break;
      default:
        // Do nothing for other keys
        break;
    }
  };

  const getSelectedItem = () => {
    let currentIndex = 0;
    for (const section of searchResults) {
      for (const item of section.items) {
        if (currentIndex === selectedIndex) {
          return item;
        }
        currentIndex++;
      }
    }
    return null;
  };

  const highlightText = (text, query) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  };

  return (
    <div className="navbar-form" ref={searchRef}>
      <div className="position-relative">
        <div className="form-group d-flex align-items-center mb-0">
          <input
            type="text"
            className="form-control"
            placeholder="Enter keyword"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (searchResults.length > 0) {
                setShowResults(true);
              }
            }}
          />
          {isSearching ? (
            <div className="btn btn-search">
              <div className="spinner-border spinner-border-sm text-white" role="status">
                <span className="visually-hidden">Searching...</span>
              </div>
            </div>
          ) : (
            <button type="button" className="btn btn-search">
              <i className="fa fa-search"></i>
            </button>
          )}
        </div>

        {showResults && searchResults.length > 0 && (
          <div className="search-results-panel" ref={resultsRef}>
            <div className="search-results-header">
              <span className="text-muted">Search Results</span>
              <button
                className="btn btn-sm btn-link text-muted p-0"
                onClick={() => setShowResults(false)}
              >
                <i className="fa fa-times"></i>
              </button>
            </div>

            {searchResults.map((section, sectionIndex) => (
              <div key={section.type} className="search-results-section">
                <div className="search-results-section-header">
                  <i className={`${section.icon} me-2`}></i>
                  <span className="fw-bold">{section.title}</span>
                  <span className="badge bg-secondary ms-2">{section.items.length}</span>
                </div>

                {section.items.map((item, itemIndex) => {
                  const globalIndex = searchResults
                    .slice(0, sectionIndex)
                    .reduce((sum, s) => sum + s.items.length, 0) + itemIndex;

                  // For branches, show view-only badge but allow navigation
                  const isViewOnly = item.isViewOnly;
                  
                  return (
                    <div
                      key={item.id}
                      className={`search-result-item ${selectedIndex === globalIndex ? 'selected' : ''}`}
                      onClick={(e) => {
                        setShowResults(false);
                        setSearchQuery('');
                        if (item.link && item.link !== '#') {
                          window.location.href = item.link;
                        }
                      }}
                      style={{
                        cursor: 'pointer'
                      }}
                    >
                      <div className="search-result-content">
                        <div
                          className="search-result-title"
                          dangerouslySetInnerHTML={{
                            __html: highlightText(item.title, searchQuery)
                          }}
                        />
                        <div className="search-result-subtitle text-muted">
                          {item.subtitle}
                          {isViewOnly && (
                            <span className="badge bg-info text-white ms-2" style={{ fontSize: '10px' }}>
                              View-only
                            </span>
                          )}
                        </div>
                      </div>
                      <i className="fa fa-chevron-right text-muted"></i>
                    </div>
                  );
                })}
              </div>
            ))}

            {searchResults.length === 0 && (
              <div className="search-no-results text-center py-3">
                <i className="fa fa-search text-muted mb-2"></i>
                <div className="text-muted">No results found for "{searchQuery}"</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalSearch;